import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { MockBadge, LanguageToggle } from "../components/ui";
import { useI18n } from "../i18n/I18nContext";
import { staleNote } from "../lib/useRateDefault";
import AIChatPanel from "./AIChatPanel";
import OnboardingTour from "../components/OnboardingTour";
import {
  onboardingOf, TASK_EVENT, START_EVENT, OPEN_AI_EVENT,
} from "../lib/onboarding";
import api from "../lib/api";
import {
  LayoutDashboard, Building2, Map, Calculator, Bookmark,
  LogOut, Sparkles, Bell, Globe2, RefreshCw, Settings as SettingsIcon,
  Activity, Code2, Sun, Menu, X, BarChart2, Users, Crosshair, Hammer,
  ReceiptText, KeyRound, Home, Zap, History, HelpCircle,
} from "lucide-react";

export default function AppShell() {
  const { user, logout, updateOnboarding } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const location = useLocation();
  const [chatOpen, setChatOpen] = useState(false);
  const [chatListing, setChatListing] = useState(null);
  const [unread, setUnread] = useState(0);
  const [lastRun, setLastRun] = useState(null);
  const [health, setHealth] = useState(null);
  const [liveMode, setLiveMode] = useState(null);
  const [rates, setRates] = useState(null);
  const [piping, setPiping] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [tourMode, setTourMode] = useState(null);   // "welcome" | "tour" | null
  const onboarding = onboardingOf(user);

  // First login after registering: offer the tour once per page load.
  useEffect(() => {
    if (onboarding.tour === "pending") setTourMode((m) => m || "welcome");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboarding.tour]);

  // Pages report checklist actions and ask for the tour or the chat through
  // window events (lib/onboarding.js), so they never import AppShell.
  useEffect(() => {
    const onTask = (e) => {
      const ob = onboardingOf(user);
      if (ob.checklist && !(ob.tasks || []).includes(e.detail)) updateOnboarding({ task: e.detail });
    };
    const onStart = () => setTourMode("tour");
    const onOpenAi = () => setChatOpen(true);
    window.addEventListener(TASK_EVENT, onTask);
    window.addEventListener(START_EVENT, onStart);
    window.addEventListener(OPEN_AI_EVENT, onOpenAi);
    return () => {
      window.removeEventListener(TASK_EVENT, onTask);
      window.removeEventListener(START_EVENT, onStart);
      window.removeEventListener(OPEN_AI_EVENT, onOpenAi);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const openHelp = () => {
    if (!onboarding.help_seen) updateOnboarding({ help_seen: true });
    setTourMode("tour");
  };

  const [aiVerdicts, setAiVerdicts] = useState(() => {
    try { return JSON.parse(localStorage.getItem("immo_aiVerdicts") || "{}"); } catch { return {}; }
  });
  const [listingSessions, setListingSessions] = useState(() => {
    try { return JSON.parse(localStorage.getItem("immo_listingSessions") || "{}"); } catch { return {}; }
  });

  // Expire entries older than 30 days on mount
  useEffect(() => {
    const TTL = 30 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    ["immo_aiVerdicts", "immo_listingSessions"].forEach((key) => {
      try {
        const stored = JSON.parse(localStorage.getItem(key) || "{}");
        const cleaned = Object.fromEntries(Object.entries(stored).filter(([, v]) => now - (v.at || 0) < TTL));
        localStorage.setItem(key, JSON.stringify(cleaned));
      } catch {}
    });
  }, []);

  const handleVerdictUpdate = (listingId, verdict) => {
    const next = { ...aiVerdicts, [listingId]: { verdict, at: Date.now() } };
    setAiVerdicts(next);
    localStorage.setItem("immo_aiVerdicts", JSON.stringify(next));
  };

  const handleSessionSaved = (listingId, sessionId) => {
    setListingSessions((prev) => {
      if (prev[listingId]?.sessionId === sessionId) return prev;
      const next = { ...prev, [listingId]: { sessionId, at: Date.now() } };
      localStorage.setItem("immo_listingSessions", JSON.stringify(next));
      return next;
    });
  };

  // Close the mobile drawer on navigation
  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  const refreshStatus = async () => {
    try {
      const { data } = await api.get("/pipeline/status");
      setLastRun(data.last_run);
    } catch {}
    try {
      const { data } = await api.get("/pipeline/health");
      setHealth(data);
    } catch {}
    try {
      const { data } = await api.get("/notifications?unread_only=true");
      setUnread(data.unread_count);
    } catch {}
    try {
      // Keep the whole envelope, not just data.rates: the band cannot be
      // rendered honestly without the stale flag that qualifies it.
      const { data } = await api.get("/rates");
      setRates(data.available ? data : null);
    } catch {}
    if (user?.role === "admin") {
      try {
        const { data } = await api.get("/admin/live-mode");
        setLiveMode(data);
      } catch {}
    }
  };

  useEffect(() => {
    refreshStatus();
    const t = setInterval(refreshStatus, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [user?.role]);

  const runPipeline = async () => {
    setPiping(true);
    try { await api.post("/pipeline/run"); await refreshStatus(); }
    finally { setPiping(false); }
  };

  const nav_items = [
    { key: "dashboard", to: "/", label: t("nav.dashboard"), icon: LayoutDashboard, end: true },
    { key: "listings", to: "/listings", label: t("nav.listings"), icon: Building2 },
    { key: "mieten", to: "/mieten", label: t("nav.mieten"), icon: KeyRound },
    { key: "personalSearch", to: "/personal-search", label: t("nav.personalSearch"), icon: Home },
    { key: "markets", to: "/markets", label: t("nav.markets"), icon: Map },
    { key: "map", to: "/map", label: t("nav.map"), icon: Globe2 },
    { key: "underwriter", to: "/objekt-cockpit", label: t("nav.underwriter"), icon: Calculator },
    { key: "rentcalc", to: "/mietpreis", label: t("nav.rentCalculator"), icon: ReceiptText },
    { key: "bewerter", to: "/bewerten", label: t("nav.bewerter"), icon: Crosshair },
    { key: "quickUnderwrite", to: "/underwrite", label: t("nav.quickUnderwrite"), icon: Zap },
    { key: "fixflip", to: "/fixflip", label: t("nav.fixflip"), icon: Hammer },
    { key: "alerts", to: "/alerts", label: t("nav.alerts"), icon: Bell, badge: unread },
    { key: "briefs", to: "/briefs", label: t("nav.briefs"), icon: Sun },
    { key: "watchlist", to: "/watchlist", label: t("nav.watchlist"), icon: Bookmark },
    { key: "archive", to: "/archive", label: t("nav.archive"), icon: History },
    { key: "analysis", to: "/analysis", label: t("nav.analysis"), icon: BarChart2 },
    { key: "settings", to: "/settings", label: t("nav.settings"), icon: SettingsIcon },
    { key: "api", to: "/apidocs", label: t("nav.api"), icon: Code2 },
    ...(user?.role === "admin" ? [
      { key: "diagnostics", to: "/diagnostics", label: t("nav.diagnostics"), icon: Activity },
      { key: "admin", to: "/admin", label: t("nav.admin"), icon: Users },
    ] : []),
  ];

  // Primary 4 tabs for the mobile bottom bar
  const bottomTabs = [
    { key: "dashboard", to: "/", label: t("nav.home"), icon: LayoutDashboard, end: true },
    { key: "listings", to: "/listings", label: t("nav.listings"), icon: Building2 },
    { key: "markets", to: "/markets", label: t("nav.markets"), icon: Map },
    { key: "alerts", to: "/alerts", label: t("nav.alerts"), icon: Bell, badge: unread },
  ];

  // Admin-configured hidden features (Admin → Features tab) — never applies
  // to admin accounts themselves; see backend get_hidden_features().
  const hiddenFeatures = new Set(user?.hidden_features || []);
  const visible_nav_items = nav_items.filter(({ key }) => !hiddenFeatures.has(key));
  const visible_bottom_tabs = bottomTabs.filter(({ key }) => !hiddenFeatures.has(key));

  // Health-derived display helpers
  const healthStatus = health?.status ?? "unknown";
  const dataAgeH = health?.hours_since_update ?? null;
  const lastScrapeLabel = health?.last_updated
    ? new Date(health.last_updated).toLocaleString("en-GB")
    : t("shell.noScrapeYet");
  const dataAgeLabel = dataAgeH == null ? "—"
    : dataAgeH < 1    ? `${Math.round(dataAgeH * 60)}m`
    : dataAgeH < 25   ? `${Math.round(dataAgeH)}h`
    : `${Math.round(dataAgeH)}h ⚠`;
  const dataAgeClass = healthStatus === "ok"    ? "text-signal-green"
    : healthStatus === "stale"  ? "text-signal-yellow"
    : "text-signal-red";
  const healthDotClass = healthStatus === "ok"   ? "bg-signal-green"
    : healthStatus === "stale" ? "bg-signal-yellow"
    : "bg-signal-red";
  // 10y fixed-rate band from the manually maintained rates.json (see Zins-Puls in
  // Briefs). Rendered only when a real figure exists — never a hardcoded default.
  const zinsBand = rates?.rates?.fix_10y_privat;
  const zinsLabel = zinsBand?.low != null && zinsBand?.high != null
    ? `${(zinsBand.low * 100).toFixed(2).replace(".", ",")}–${(zinsBand.high * 100).toFixed(2).replace(".", ",")}%`
    : null;
  // A stale band still gets shown — hiding it would just leave a gap — but it
  // must not read as the current market rate. Same yellow the data-age cell
  // uses, plus the reason in the tooltip: "old" and "the benchmark has moved
  // out from under it" are different problems and the fix differs.
  const zinsStale = Boolean(rates?.stale);
  const zinsTooltip = zinsStale
    ? staleNote(rates, t, "shell")
    : t("shell.zinsTooltip", { date: rates?.rates?.as_of ? new Date(rates.rates.as_of).toLocaleDateString("de-AT") : "—" });
  const showBanner = health && healthStatus !== "ok" && healthStatus !== "unknown";
  const bannerMsg = healthStatus === "stale"
    ? t("shell.bannerStale", { hours: dataAgeH != null ? Math.round(dataAgeH) : "?" })
    : t("shell.bannerEmpty");

  return (
    <div className="min-h-screen bg-bg text-white relative">
      {/* TOP HEADER */}
      <header className="sticky top-0 z-40 border-b border-border bg-bg/80 backdrop-blur">
        <div className="grid grid-cols-12 items-center">

          {/* Logo — expands on mobile to take status-strip space */}
          <div className="col-span-6 md:col-span-3 lg:col-span-2 flex items-center gap-3 px-5 py-3 border-r border-border">
            <div className="w-7 h-7 bg-signal-green grid place-items-center flex-shrink-0">
              <div className="w-2.5 h-2.5 bg-black" />
            </div>
            <div>
              <div className="font-heading font-black text-sm tracking-tight leading-none">immo-watch</div>
              <div className="overline mt-0.5">{t("shell.tagline")}</div>
            </div>
          </div>

          {/* Status strip — hidden on mobile, visible md+ */}
          <div data-testid="topbar-status" className="hidden md:flex col-span-6 lg:col-span-7 items-center divide-x divide-border text-[10px] font-mono overflow-x-auto">
            <StatusCell label={t("shell.listings")} value={health?.listing_count ?? lastRun?.listing_count ?? "—"} />
            <StatusCell label={t("shell.green")} value={lastRun?.green_count ?? "—"} valueClass="text-signal-green" />
            <StatusCell label={t("shell.markets")} value={lastRun?.market_count ?? "—"} />
            {zinsLabel && (
              <StatusCell
                label={t("shell.zins")}
                value={zinsStale ? `${zinsLabel} ⚠` : zinsLabel}
                valueClass={zinsStale ? "text-signal-yellow" : "text-white"}
                title={zinsTooltip}
              />
            )}
            <div
              className="flex items-center gap-1.5 px-3 py-3 whitespace-nowrap"
              title={t("shell.lastScrapeTooltip", { time: lastScrapeLabel })}
            >
              <span className={`block w-1.5 h-1.5 flex-shrink-0 ${healthDotClass} dot-pulse`} />
              <span className="text-text-muted uppercase tracking-widest2">{t("shell.data")}</span>
              <span className={`num ${dataAgeClass}`}>{dataAgeLabel}</span>
            </div>
            <StatusCell label={t("shell.source")} value={(health?.source ?? "—").toUpperCase()} valueClass="text-text-secondary" />
            {liveMode && (
              <div
                data-testid="topbar-live-mode-chip"
                className={`flex items-center gap-1.5 px-3 py-3 whitespace-nowrap ${
                  liveMode.effective ? "text-signal-green" : "text-signal-yellow"
                }`}
                title={t("shell.liveModeTooltip", { override: liveMode.runtime_override, env: liveMode.env_use_live_screen })}
              >
                <span className={`block w-1.5 h-1.5 ${liveMode.effective ? "bg-signal-green" : "bg-signal-yellow"} dot-pulse`} />
                <span className="text-text-muted uppercase tracking-widest2">{t("shell.mode")}</span>
                <span className="num">{liveMode.effective ? "LIVE" : "SYNTH"}</span>
              </div>
            )}
            {user?.role === "admin" && (
              <button
                onClick={runPipeline}
                data-testid="topbar-run-pipeline"
                disabled={piping}
                title={t("shell.runPipelineTitle")}
                className="flex items-center gap-1.5 px-3 py-3 text-signal-green hover:bg-signal-green/10 disabled:opacity-40 whitespace-nowrap"
              >
                <RefreshCw size={11} className={piping ? "animate-spin" : ""} />
                {piping ? t("shell.running") : t("shell.refresh")}
              </button>
            )}
          </div>

          {/* User area — expands on mobile */}
          <div className="col-span-6 md:col-span-3 flex items-center justify-end gap-2 px-4 py-3 border-l border-border">
            <LanguageToggle className="hidden sm:inline-flex" />
            <button
              type="button"
              data-testid="topbar-help"
              onClick={openHelp}
              className="relative p-2 text-text-muted hover:text-white"
              title={t("onboarding.helpTitle")}
              aria-label={t("onboarding.helpTitle")}
            >
              <HelpCircle size={16} />
              {!onboarding.help_seen && (
                <span
                  data-testid="topbar-help-new"
                  className="absolute -top-0.5 -right-1 bg-signal-green text-black text-[8px] font-bold font-mono px-1 leading-[14px]"
                >
                  {t("onboarding.helpNew")}
                </span>
              )}
            </button>
            <button
              data-testid="topbar-bell"
              onClick={() => nav("/alerts")}
              className="relative p-2 text-text-muted hover:text-white"
              title={t("shell.alertsTitle")}
            >
              <Bell size={16} />
              {unread > 0 && (
                <span
                  data-testid="bell-unread-badge"
                  className="absolute top-1 right-1 min-w-[14px] h-3.5 bg-signal-green text-black text-[9px] font-bold font-mono grid place-items-center px-1"
                >
                  {unread}
                </span>
              )}
            </button>
            <MockBadge />
            <button
              data-testid="open-ai-chat"
              onClick={() => setChatOpen(true)}
              className="ml-1 inline-flex items-center gap-1.5 border border-signal-green/40 text-signal-green hover:bg-signal-green hover:text-black font-mono text-[10px] uppercase tracking-widest2 px-2.5 py-1.5 transition-colors"
            >
              <Sparkles size={12} />
              <span className="hidden sm:inline">IMMO-AI</span>
            </button>
            {/* Desktop: avatar + logout */}
            <div className="hidden md:flex items-center gap-2 ml-2 pl-3 border-l border-border">
              <div className="w-7 h-7 bg-zinc-800 grid place-items-center text-[10px] font-mono">
                {(user?.email || "").slice(0, 2).toUpperCase()}
              </div>
              <button
                data-testid="logout-btn"
                onClick={async () => { await logout(); nav("/login"); }}
                className="text-text-muted hover:text-signal-red"
                title={t("shell.logout")}
              >
                <LogOut size={16} />
              </button>
            </div>
            {/* Mobile: hamburger opens the full nav drawer */}
            <button
              className="md:hidden p-2 text-text-muted hover:text-white"
              onClick={() => setMoreOpen(true)}
              aria-label={t("shell.openNavigation")}
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* Stale / mock data banner */}
      {showBanner && (
        <div className="bg-signal-yellow/10 border-b border-signal-yellow/30 px-5 py-2 text-[11px] font-mono text-signal-yellow flex items-center gap-2">
          <span className="flex-1">{bannerMsg}</span>
          {user?.role === "admin" && (
            <button onClick={runPipeline} disabled={piping} className="underline hover:no-underline opacity-80 hover:opacity-100">
              {piping ? t("shell.runningLower") : t("shell.refreshNow")}
            </button>
          )}
        </div>
      )}

      {/* MAIN GRID */}
      <div className="grid grid-cols-12 min-h-[calc(100vh-49px)]">
        {/* SIDEBAR — desktop only */}
        <aside className="col-span-2 hidden md:flex flex-col border-r border-border min-h-full">
          <nav className="flex-1 py-4">
            {visible_nav_items.map(({ key, to, label, icon: Icon, end, badge }) => (
              <NavLink
                key={key}
                to={to}
                end={end}
                data-testid={`nav-${key}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-3 text-xs font-mono uppercase tracking-widest2 border-l-2 transition-colors ${
                    isActive
                      ? "border-l-signal-green text-white bg-surfaceHover"
                      : "border-l-transparent text-text-muted hover:text-white hover:bg-surface"
                  }`
                }
              >
                <Icon size={14} />
                <span className="flex-1">{label}</span>
                {badge ? (
                  <span className="num text-[10px] bg-signal-green text-black px-1.5 py-0.5">{badge}</span>
                ) : null}
              </NavLink>
            ))}
          </nav>
          <div className="border-t border-border p-4">
            <div className="overline">{t("shell.console")}</div>
            <div className="mt-2 flex items-center gap-2 text-[10px] font-mono">
              <span className={`block w-1.5 h-1.5 ${healthDotClass} dot-pulse`} />
              <span className="text-text-secondary">{healthStatus} · {(health?.source ?? lastRun?.source ?? "—").toUpperCase()}</span>
            </div>
            <div className="mt-1 text-[10px] font-mono text-text-muted">
              {health?.listing_count ?? lastRun?.listing_count
                ? t("shell.consoleSummary", { count: health?.listing_count ?? lastRun?.listing_count, age: dataAgeLabel })
                : t("shell.buildVersion")}
            </div>
            <div className="mt-1 text-[10px] font-mono text-text-muted" title={t("shell.lastScrapeNote")}>
              {t("shell.lastScrape", { time: lastScrapeLabel })}
            </div>
          </div>
        </aside>

        {/* CONTENT — bottom padding on mobile to clear bottom nav */}
        <main className={`col-span-12 md:col-span-10 min-h-full pb-20 md:pb-0 transition-all duration-300 ${chatOpen ? "lg:pr-[560px]" : ""}`}>
          <Outlet context={{ openChat: (listing = null) => { setChatListing(listing); setChatOpen(true); }, refreshStatus, liveMode, setLiveMode, aiVerdicts, listingSessions }} />
        </main>
      </div>

      {/* MOBILE BOTTOM TAB BAR */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-bg/95 backdrop-blur border-t border-border flex"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {visible_bottom_tabs.map(({ key, to, label, icon: Icon, end, badge }) => (
          <NavLink
            key={key}
            to={to}
            end={end}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-1 py-2 min-h-[56px] text-[9px] font-mono uppercase tracking-widest2 transition-colors ${
                isActive ? "text-signal-green" : "text-text-muted"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <div className="relative">
                  <Icon size={20} strokeWidth={isActive ? 2.5 : 1.5} />
                  {badge > 0 && (
                    <span className="absolute -top-1 -right-2 min-w-[14px] h-3.5 bg-signal-green text-black text-[9px] font-bold font-mono grid place-items-center px-1">
                      {badge}
                    </span>
                  )}
                </div>
                <span>{label}</span>
              </>
            )}
          </NavLink>
        ))}
        {/* More button */}
        <button
          onClick={() => setMoreOpen(true)}
          className="flex-1 flex flex-col items-center justify-center gap-1 py-2 min-h-[56px] text-[9px] font-mono uppercase tracking-widest2 text-text-muted"
        >
          <Menu size={20} strokeWidth={1.5} />
          <span>{t("nav.more")}</span>
        </button>
      </nav>

      {/* MOBILE MORE DRAWER */}
      {moreOpen && (
        <>
          {/* Backdrop */}
          <div
            className="md:hidden fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
            onClick={() => setMoreOpen(false)}
          />
          {/* Sheet */}
          <div
            className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-bg border-t border-border rounded-t-xl overflow-hidden"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <span className="font-mono text-xs uppercase tracking-widest2 text-text-secondary">{t("nav.navigation")}</span>
              <button
                onClick={() => setMoreOpen(false)}
                className="text-text-muted hover:text-white p-1"
                aria-label={t("common.close")}
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-5 py-3 border-b border-border flex items-center gap-2 text-[10px] font-mono">
              <span className={`block w-1.5 h-1.5 flex-shrink-0 ${healthDotClass} dot-pulse`} />
              <span className="text-text-muted">{t("shell.lastScrape", { time: lastScrapeLabel })}</span>
            </div>
            <div className="overflow-y-auto max-h-[60vh]">
              {visible_nav_items.map(({ key, to, label, icon: Icon, end, badge }) => (
                <NavLink
                  key={key}
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    `flex items-center gap-4 px-5 py-4 text-xs font-mono uppercase tracking-widest2 border-l-2 transition-colors ${
                      isActive
                        ? "border-l-signal-green text-white bg-surfaceHover"
                        : "border-l-transparent text-text-muted"
                    }`
                  }
                >
                  <Icon size={16} />
                  <span className="flex-1">{label}</span>
                  {badge > 0 && (
                    <span className="num text-[10px] bg-signal-green text-black px-1.5 py-0.5">{badge}</span>
                  )}
                </NavLink>
              ))}
            </div>
            <div className="border-t border-border px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-zinc-800 grid place-items-center text-[10px] font-mono flex-shrink-0">
                  {(user?.email || "").slice(0, 2).toUpperCase()}
                </div>
                <span className="text-[10px] font-mono text-text-secondary truncate max-w-[160px]">
                  {user?.email}
                </span>
              </div>
              <button
                data-testid="logout-btn-mobile"
                onClick={async () => { await logout(); nav("/login"); }}
                className="flex items-center gap-2 text-text-muted hover:text-signal-red text-[10px] font-mono uppercase tracking-widest2"
              >
                <LogOut size={14} />
                <span>{t("shell.logout")}</span>
              </button>
            </div>
          </div>
        </>
      )}

      <OnboardingTour
        mode={tourMode}
        user={user}
        onStart={() => setTourMode("tour")}
        onLater={() => { setTourMode(null); if (onboarding.tour === "pending") updateOnboarding({ tour: "later" }); }}
        onFinish={() => { setTourMode(null); if (onboarding.tour !== "done") updateOnboarding({ tour: "done" }); }}
      />

      {/* AI CHAT PANEL */}
      <AIChatPanel
        open={chatOpen}
        listing={chatListing}
        onClose={() => { setChatOpen(false); setChatListing(null); }}
        savedSessionId={chatListing ? listingSessions[chatListing.id]?.sessionId : null}
        onSessionSaved={handleSessionSaved}
        onVerdictUpdate={handleVerdictUpdate}
      />
    </div>
  );
}

function StatusCell({ label, value, valueClass = "text-white", title }) {
  return (
    <div className="flex items-center gap-2 px-4 py-3 whitespace-nowrap" title={title}>
      <span className="text-text-muted uppercase tracking-widest2">{label}</span>
      <span className={`num ${valueClass}`}>{value}</span>
    </div>
  );
}
